// Ported from dev-scrips-pm-react src/shared/ui/components/payer-photo-uploader.tsx (PROD-1119 Patients UI port).
/**
 * PayerPhotoUploader — front/back card-photo tile for insurance &
 * corporate payer editors.
 *
 *   - No photo: dashed affordance reading "Scan {side} side" under the camera glyph, matching
 *     Flutter (`edit_health_insurance_widget.dart:304-326`); opens the file
 *     picker directly.
 *   - Has photo: clicking the thumbnail opens a preview dialog with
 *     Change (re-pick) / Remove (clear) / Close (keep) actions. The
 *     thumbnail itself only opens the dialog; uploads fire from the
 *     dialog's Change button or the empty-state.
 *
 * Flutter parity: `upload_image_dialog.dart`.
 *
 * Extracted from `scheduling/.../edit-insurance-view.tsx` so the
 * scheduling appointment-insurance editor and the patient-module
 * Contact-Details insurance / corporate sections share one
 * implementation (single source of truth — no per-domain copy).
 *
 * The upload `area` is a prop because callers differ:
 *   - patient-module insurance + corporate card photos → `PATIENT`
 *     (Flutter `imageAreaPatient`, confirmed against the 2026-06-05
 *     /api/Master/UploadImage dev curl `"area":"PATIENT"`).
 *   - scheduling appointment-insurance editor → `INSURANCE_CARD`
 *     (its existing behaviour, preserved).
 *
 * PHI: the base64 lives only in the local `dataUrl` inside `onPick`;
 * only the server-issued URL is bubbled up via `onUploaded`. See
 * `use-upload-image.ts` for the full handling contract.
 */

import { useRef, useState } from 'react';
// GAP: no DS icon for taking / uploading a photo (Camera); lucide in both modes (design sheet §3.1, logged not substituted).
import { Camera } from 'lucide-react';
import { Button, Modal } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import { useUploadImage, readFileAsBase64 } from '@/services/common/use-upload-image';

/**
 * The only two `ImageArea` values this tile is ever used with. Pinned to a
 * narrow literal union (not the full `ImageArea`) so passing any other area
 * is a compile error rather than a silent runtime branch (Samer review on
 * PR #495). Patient-module insurance + corporate card photos → `PATIENT`;
 * scheduling appointment-insurance editor → `INSURANCE_CARD`.
 */
export type PayerCardArea = 'PATIENT' | 'INSURANCE_CARD';

export interface PayerPhotoUploaderProps {
  url: string | null | undefined;
  /** Short label for the upload button + alt text — e.g. "Front Photo". */
  label: string;
  /** Card side for the dialog title — e.g. "front" or "back". */
  side: 'front' | 'back';
  /** Dialog title noun — "insurance" or "corporate". */
  titleNoun: string;
  /** Upload area tag — `PATIENT` for patient module, `INSURANCE_CARD` for scheduling. */
  area: PayerCardArea;
  onUploaded: (url: string) => void;
  onClear: () => void;
  disabled?: boolean;
}

export function PayerPhotoUploader({
  url,
  label,
  side,
  titleNoun,
  area,
  onUploaded,
  onClear,
  disabled,
}: PayerPhotoUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const uploadMutation = useUploadImage();

  const onPick = async (file: File | undefined) => {
    if (!file) return;
    try {
      const dataUrl = await readFileAsBase64(file);
      // `useUploadImage` returns `string | undefined`: the backend replies
      // with an EMPTY BODY on dedup / re-upload (and currently on dev for
      // ALL areas — see use-upload-image.ts header), which parses to
      // `undefined`. We must NOT optimistically set a non-existent URL, or
      // the thumbnail would 404 in prod when the API behaves differently.
      // Guard: only commit a truthy, non-empty URL; otherwise keep the
      // current state untouched (Samer review on PR #495).
      const uploadedUrl = await uploadMutation.mutateAsync({
        base64String: dataUrl,
        area,
      });
      if (typeof uploadedUrl === 'string' && uploadedUrl.trim() !== '') {
        onUploaded(uploadedUrl);
        setDialogOpen(false);
      }
      // else: empty body — leave the existing photo (if any) in place.
    } catch {
      // Server-error toast surfaces via the global handler.
    }
  };

  const handleRemove = () => {
    onClear();
    setDialogOpen(false);
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => onPick(e.target.files?.[0])}
      />
      {url ? (
        <>
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            disabled={disabled}
            className="relative h-20 w-full rounded-md overflow-hidden border border-border-default bg-grey-100"
            aria-label={`Preview ${label}`}
          >
            <img src={url} alt={label} className="h-full w-full object-cover" />
          </button>
          {/* shadcn Dialog (controlled) → DS Modal. Title is the string
              header; the preview image is the body; Remove/Change are the
              footer. `sm:max-w-lg` → size="lg". */}
          <Modal
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            title={`Change ${titleNoun} ${side} photo`}
            size="lg"
            footer={
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="tertiary"
                  onClick={handleRemove}
                  className="text-status-error hover:text-status-error"
                  disabled={uploadMutation.isPending || disabled}
                >
                  Remove
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => inputRef.current?.click()}
                  disabled={uploadMutation.isPending || disabled}
                >
                  {uploadMutation.isPending ? 'Uploading…' : 'Change'}
                </Button>
              </div>
            }
          >
            <div className="rounded-md border border-border-default bg-grey-100 overflow-hidden">
              <img
                src={url}
                alt={label}
                className="w-full max-h-media-preview object-contain bg-surface-card"
              />
            </div>
          </Modal>
        </>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploadMutation.isPending || disabled}
          // The visible text is Flutter's ("Scan front side"); the accessible name keeps the
          // host's own label so screen readers still hear which card face this is.
          aria-label={`Add ${label}`}
          className={cn(
            'h-20 w-full rounded-md border border-dashed flex flex-col items-center justify-center gap-1.25',
            uploadMutation.isPending || disabled
              ? 'border-border-default text-text-muted'
              : 'border-border-default text-text-muted hover:border-brand-primary hover:text-brand-primary',
          )}
        >
          {/* Flutter's empty slot, followed to the letter (`edit_health_insurance_widget.dart`
              :304-326, and the same shape in the claim-workbench twin): a centred COLUMN of the
              camera glyph (43x34), a 5px gap, then "Scan {side} side" at 13px in the field-hint
              colour. Ours had a 16px icon inline with "Add Front Photo" — same idea, different
              presentation. Andrew asked for the Flutter behaviour here rather than dropping the
              icon (2026-08-20). */}
          <Camera className="size-6" aria-hidden />
          <span className="text-size-sm leading-none">
            {uploadMutation.isPending ? 'Uploading…' : `Scan ${side} side`}
          </span>
        </button>
      )}
    </div>
  );
}
